<template>
  <v-container
    fundamental
    fluid
    grid-list-xl
    pt-0
    class="req-ticket-form req-skin">
    <v-layout wrap>
      <v-flex xs12>
        <material-card
          title="01 基本資訊"
          color="deep-orange"
          class="card-padding v-card__first">
          <v-alert
            :value="!!readOnlyReason"
            color="general"
            outline
            class="mb-2">{{ readOnlyReason }}</v-alert>
          <div class="caption mb-1">需求類型 *</div>
          <v-radio-group
            v-model="form.reqType"
            :disabled="!formEditable || !!form.version"
            row
            class="mt-0">
            <v-radio
              label="新增功能"
              value="NEW_FEATURE"/>
            <v-radio
              label="修改功能"
              value="MODIFY"/>
            <v-radio
              label="問題回報"
              value="ISSUE"/>
          </v-radio-group>
          <v-layout wrap>
            <v-flex
              v-if="showTicketNo"
              xs12
              lg4>
              <v-text-field
                :value="form.ticketNo"
                :class="fieldClass"
                label="需求單號"
                disabled
              />
            </v-flex>
            <v-flex
              v-if="versionItems.length > 1 && !secondary"
              xs12
              lg4>
              <v-select
                v-model="selectedVersion"
                :items="versionItems"
                :class="fieldClass"
                label="切換版本"
                @change="changeVersion"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-text-field
                :value="versionText"
                :class="fieldClass"
                label="版本"
                disabled
              />
            </v-flex>
            <v-flex
              v-if="form.reqType !== 'NEW_FEATURE'"
              xs12
              lg6>
              <v-autocomplete
                v-model="form.relatedTicketNo"
                :items="relatedItems"
                :class="fieldClass"
                :disabled="!formEditable"
                label="關聯需求單號 *"
                clearable
              />
            </v-flex>
            <v-flex
              xs12
              lg6>
              <v-text-field
                v-model="form.title"
                :class="fieldClass"
                :disabled="!formEditable"
                label="需求標題 *"
                placeholder="請輸入需求標題"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-select
                v-model="form.priority"
                :items="priorityItems"
                :class="fieldClass"
                :disabled="!formEditable"
                label="優先級 *"
                placeholder="請選擇"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-text-field
                v-model="form.requesterDeptName"
                :class="fieldClass"
                label="提出部門（自動帶入）"
                disabled
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-text-field
                v-model="form.requesterName"
                :class="fieldClass"
                label="提出人（自動帶入）"
                disabled
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-text-field
                v-model="form.extension"
                :class="fieldClass"
                :disabled="!formEditable"
                label="分機"
                placeholder="分機號碼"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-menu
                v-model="dateMenu.reqDate"
                :close-on-content-click="false"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                max-width="290px">
                <v-text-field
                  slot="activator"
                  v-model="form.reqDate"
                  :class="fieldClass"
                  :disabled="!formEditable"
                  label="需求日期 *"
                  prepend-icon="event"
                  readonly
                  clearable
                />
                <v-date-picker
                  v-model="form.reqDate"
                  :color="theme==='dark'?'danger':'general'"
                  no-title
                  scrollable
                  @input="dateMenu.reqDate = false"
                />
              </v-menu>
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-menu
                v-model="dateMenu.planStartDate"
                :close-on-content-click="false"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                max-width="290px">
                <v-text-field
                  slot="activator"
                  v-model="form.planStartDate"
                  :class="fieldClass"
                  :disabled="!formEditable"
                  label="預計開始日期"
                  prepend-icon="event"
                  readonly
                  clearable
                />
                <v-date-picker
                  v-model="form.planStartDate"
                  :color="theme==='dark'?'danger':'general'"
                  no-title
                  scrollable
                  @input="dateMenu.planStartDate = false"
                />
              </v-menu>
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-menu
                v-model="dateMenu.planEndDate"
                :close-on-content-click="false"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                max-width="290px">
                <v-text-field
                  slot="activator"
                  v-model="form.planEndDate"
                  :class="fieldClass"
                  :disabled="!formEditable"
                  label="預計完成日期 *"
                  prepend-icon="event"
                  readonly
                  clearable
                />
                <v-date-picker
                  v-model="form.planEndDate"
                  :color="theme==='dark'?'danger':'general'"
                  no-title
                  scrollable
                  @input="dateMenu.planEndDate = false"
                />
              </v-menu>
            </v-flex>
          </v-layout>
        </material-card>
      </v-flex>

      <v-flex xs12>
        <material-card
          title="02 需求背景"
          color="deep-orange"
          class="card-padding">
          <v-textarea
            v-model="bg.currentProblem"
            :class="fieldClass"
            :disabled="!formEditable"
            label="目前遇到的問題 *"
            rows="3"
          />
          <v-textarea
            v-model="bg.goal"
            :class="fieldClass"
            :disabled="!formEditable"
            label="希望達成的目標 *"
            rows="3"
          />
          <div class="caption mb-1">需求來源 *</div>
          <v-radio-group
            v-model="bg.reqSource"
            :disabled="!formEditable"
            row
            class="mt-0">
            <v-radio
              label="自己部門"
              value="自己部門"/>
            <v-radio
              label="工廠"
              value="工廠"/>
            <v-radio
              label="主管"
              value="主管"/>
            <v-radio
              label="其他"
              value="其他"/>
          </v-radio-group>
          <v-text-field
            v-if="bg.reqSource === '其他'"
            v-model="bg.reqSourceRemark"
            :class="fieldClass"
            :disabled="!formEditable"
            label="說明"
          />
          <div class="subheading mt-3 mb-2">名詞說明</div>
          <v-layout
            v-for="(term, index) in termList"
            :key="'term' + index"
            wrap>
            <v-flex
              xs12
              lg4>
              <v-text-field
                v-model="term.termName"
                :class="fieldClass"
                :disabled="!formEditable"
                label="名詞"
              />
            </v-flex>
            <v-flex
              xs12
              lg6>
              <v-text-field
                v-model="term.termDesc"
                :class="fieldClass"
                :disabled="!formEditable"
                label="說明"
              />
            </v-flex>
            <v-flex
              xs12
              lg2>
              <v-btn
                v-if="formEditable"
                flat
                color="red"
                @click="termList.splice(index, 1)">移除</v-btn>
            </v-flex>
          </v-layout>
          <v-btn
            v-if="formEditable"
            flat
            color="general"
            @click="termList.push({ termName: '', termDesc: '' })">
            <v-icon class="mr-2">mdi-plus</v-icon>新增名詞
          </v-btn>
          <v-textarea
            v-model="bg.noDoImpact"
            :class="fieldClass"
            :disabled="!formEditable"
            label="不作此需求造成的影響"
            rows="2"
            class="mt-3"
          />
        </material-card>
      </v-flex>

      <v-flex
        v-if="form.reqType === 'NEW_FEATURE'"
        xs12>
        <material-card
          title="03 資料來源（新增功能）"
          color="deep-orange"
          class="card-padding">
          <v-layout wrap>
            <v-flex
              xs12
              lg4>
              <v-text-field
                v-model="bg.factoryName"
                :class="fieldClass"
                :disabled="!formEditable"
                label="工廠/供應商名稱 *"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-text-field
                v-model="bg.dataFormat"
                :class="fieldClass"
                :disabled="!formEditable"
                label="資料格式 *"
                placeholder="例如 Excel"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-text-field
                v-model="bg.uploadFreq"
                :class="fieldClass"
                :disabled="!formEditable"
                label="上傳頻率 *"
                placeholder="例如每天"
              />
            </v-flex>
          </v-layout>
          <v-textarea
            v-model="bg.sampleLogic"
            :class="fieldClass"
            :disabled="!formEditable"
            label="提供 sample 頁資料邏輯 *"
            rows="3"
          />
          <file-block
            :files="filesOf('DS_SAMPLE')"
            :editable="formEditable"
            label="上傳 sample 檔案 *"
            @add="pickFile('DS_SAMPLE')"
            @download="downloadFile"
            @remove="removeFile"
          />
          <div class="mt-2 mb-2">
            <div class="caption mb-1">來源資料規範與異常選單（選好一個選項後填寫右側）</div>
            <v-btn
              color="general"
              @click="questionDialog = true">填寫來源資料規範與異常</v-btn>
          </div>
        </material-card>
      </v-flex>

      <v-flex
        v-if="form.reqType === 'NEW_FEATURE'"
        xs12>
        <material-card
          title="04 業務處理（新增功能）"
          color="deep-orange"
          class="card-padding">
          <v-checkbox
            :input-value="display.needChart === 1"
            :disabled="!formEditable"
            label="是否需要圖表"
            color="general"
            hide-details
            @change="display.needChart = $event ? 1 : 0"
          />
          <v-layout
            v-if="display.needChart === 1"
            wrap
            align-center>
            <v-flex
              v-for="item in chartTypeItems"
              :key="item.value"
              xs12
              lg3>
              <v-checkbox
                :input-value="display.chartTypeList.indexOf(item.value) >= 0"
                :disabled="!formEditable"
                :label="item.text"
                color="general"
                hide-details
                @change="toggleChart(item.value, $event)"
              />
            </v-flex>
            <v-flex
              xs12
              lg6>
              <v-text-field
                v-model="display.chartOther"
                :class="fieldClass"
                :disabled="!formEditable"
                label="其他說明"
              />
            </v-flex>
          </v-layout>
          <v-textarea
            v-if="display.needChart === 1"
            v-model="display.chartAxisDesc"
            :class="fieldClass"
            :disabled="!formEditable"
            label="圖表軸說明（X/Y，若有 Z、維度、額外統計）"
            rows="2"
          />
          <file-block
            v-if="display.needChart === 1"
            :files="filesOf('CHART_SAMPLE')"
            :editable="formEditable"
            label="上傳圖表範例"
            @add="pickFile('CHART_SAMPLE')"
            @download="downloadFile"
            @remove="removeFile"
          />
          <div
            v-if="display.needChart === 1"
            class="caption mt-2">圖表與列表的關係</div>
          <v-radio-group
            v-if="display.needChart === 1"
            v-model="display.chartListRelation"
            :disabled="!formEditable"
            row
            class="mt-0">
            <v-radio
              label="圖表獨立展示"
              value="INDEPENDENT"/>
            <v-radio
              label="點擊圖表連動列表"
              value="LINK_TO_LIST"/>
            <v-radio
              label="無需關聯"
              value="NONE"/>
          </v-radio-group>
          <v-checkbox
            :input-value="display.needExport === 1"
            :disabled="!formEditable"
            label="是否需要匯出資料"
            color="general"
            hide-details
            @change="display.needExport = $event ? 1 : 0"
          />
          <template v-if="display.needExport === 1">
            <div class="caption mt-2">匯出是否與頁面展示一致</div>
            <v-radio-group
              v-model="display.exportSameAsPage"
              :disabled="!formEditable"
              row
              class="mt-0">
              <v-radio
                :value="1"
                label="是"/>
              <v-radio
                :value="0"
                label="否"/>
            </v-radio-group>
            <div class="caption">匯出是否額外增加隱藏欄位</div>
            <v-radio-group
              v-model="display.exportExtraHidden"
              :disabled="!formEditable"
              row
              class="mt-0">
              <v-radio
                :value="1"
                label="是"/>
              <v-radio
                :value="0"
                label="否"/>
            </v-radio-group>
            <v-textarea
              v-model="display.exportRemark"
              :class="fieldClass"
              :disabled="!formEditable"
              label="匯出特殊格式或規則"
              rows="2"
            />
            <file-block
              :files="filesOf('EXPORT_SAMPLE')"
              :editable="formEditable"
              label="上傳匯出範例"
              @add="pickFile('EXPORT_SAMPLE')"
              @download="downloadFile"
              @remove="removeFile"
            />
          </template>
          <div class="subheading mt-3">查詢條件</div>
          <v-layout
            v-for="(row, index) in queryCondList"
            :key="'q' + index"
            wrap>
            <v-flex
              xs12
              lg3>
              <v-text-field
                v-model="row.headerName"
                :class="fieldClass"
                :disabled="!formEditable"
                label="表頭條件名稱"
                placeholder="例如 ModeName"
              />
            </v-flex>
            <v-flex
              xs12
              lg3>
              <v-select
                v-model="row.requiredBasic"
                :items="yesNoItems"
                :class="fieldClass"
                :disabled="!formEditable"
                label="是否必選基本搜尋條件"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-text-field
                v-model="row.condDesc"
                :class="fieldClass"
                :disabled="!formEditable"
                label="查詢條件說明"
              />
            </v-flex>
            <v-flex
              xs12
              lg2>
              <v-btn
                v-if="formEditable"
                flat
                color="red"
                @click="queryCondList.splice(index, 1)">移除</v-btn>
            </v-flex>
          </v-layout>
          <v-btn
            v-if="formEditable"
            flat
            color="general"
            @click="queryCondList.push({ headerName: '', requiredBasic: 0, condDesc: '' })">
            <v-icon class="mr-2">mdi-plus</v-icon>新增查詢條件
          </v-btn>
          <div class="subheading mt-3">結果欄位</div>
          <v-layout
            v-for="(row, index) in resultColList"
            :key="'c' + index"
            wrap>
            <v-flex
              xs12
              lg5>
              <v-text-field
                v-model="row.columnName"
                :class="fieldClass"
                :disabled="!formEditable"
                label="結果欄位名稱"
              />
            </v-flex>
            <v-flex
              xs12
              lg5>
              <v-text-field
                v-model="row.columnDesc"
                :class="fieldClass"
                :disabled="!formEditable"
                label="結果欄位說明"
              />
            </v-flex>
            <v-flex
              xs12
              lg2>
              <v-btn
                v-if="formEditable"
                flat
                color="red"
                @click="resultColList.splice(index, 1)">移除</v-btn>
            </v-flex>
          </v-layout>
          <v-btn
            v-if="formEditable"
            flat
            color="general"
            @click="resultColList.push({ columnName: '', columnDesc: '' })">
            <v-icon class="mr-2">mdi-plus</v-icon>新增結果欄位
          </v-btn>
          <div class="subheading mt-3">額外需求</div>
          <div
            v-for="(row, index) in extraList"
            :key="'e' + index">
            <v-textarea
              v-model="row.extraDesc"
              :class="fieldClass"
              :disabled="!formEditable"
              label="額外功能說明（以上需求不足時填）"
              rows="2"
            />
            <file-block
              :files="filesOfRef('EXTRA_SAMPLE', index + 1)"
              :editable="formEditable"
              label="上傳畫面"
              @add="pickFile('EXTRA_SAMPLE', index + 1)"
              @download="downloadFile"
              @remove="removeFile"
            />
            <v-btn
              v-if="formEditable"
              flat
              color="red"
              @click="extraList.splice(index, 1)">移除</v-btn>
          </div>
          <v-btn
            v-if="formEditable"
            flat
            color="general"
            @click="extraList.push({ extraDesc: '' })">
            <v-icon class="mr-2">mdi-plus</v-icon>新增額外需求
          </v-btn>
        </material-card>
      </v-flex>

      <v-flex
        v-if="form.reqType === 'MODIFY'"
        xs12>
        <material-card
          title="05 既有功能修改（修改功能）"
          color="deep-orange"
          class="card-padding">
          <div
            v-for="(row, index) in moduleList"
            :key="'m' + index"
            class="mb-3">
            <div class="subheading">功能模塊 {{ index + 1 }}</div>
            <v-text-field
              v-model="row.moduleName"
              :class="fieldClass"
              :disabled="!formEditable"
              label="功能模塊名稱 *"
            />
            <v-textarea
              v-model="row.originalBehavior"
              :class="fieldClass"
              :disabled="!formEditable"
              label="系統原有做法 *"
              rows="2"
            />
            <v-textarea
              v-model="row.expectedChange"
              :class="fieldClass"
              :disabled="!formEditable"
              label="本次希望修改內容 *"
              rows="2"
            />
            <v-textarea
              v-model="row.changeReason"
              :class="fieldClass"
              :disabled="!formEditable"
              label="修改原因說明 *"
              rows="2"
            />
            <file-block
              :files="filesOfRef('MODULE_SAMPLE', index + 1)"
              :editable="formEditable"
              label="上傳畫面"
              @add="pickFile('MODULE_SAMPLE', index + 1)"
              @download="downloadFile"
              @remove="removeFile"
            />
            <v-btn
              v-if="formEditable"
              flat
              color="red"
              @click="moduleList.splice(index, 1)">移除模塊</v-btn>
          </div>
          <v-btn
            v-if="formEditable"
            flat
            color="general"
            @click="moduleList.push({ moduleName: '', originalBehavior: '', expectedChange: '', changeReason: '' })">
            <v-icon class="mr-2">mdi-plus</v-icon>新增功能模塊
          </v-btn>
        </material-card>
      </v-flex>

      <v-flex
        v-if="form.reqType === 'ISSUE'"
        xs12>
        <material-card
          title="06 問題回報"
          color="deep-orange"
          class="card-padding">
          <div class="mb-2">請附上 BUG LIST（Excel）。問題回報送出時必填。 *</div>
          <file-block
            :files="filesOf('BUG_LIST')"
            :editable="formEditable"
            label="上傳 BUG LIST"
            @add="pickFile('BUG_LIST')"
            @download="downloadFile"
            @remove="removeFile"
          />
        </material-card>
      </v-flex>

      <v-flex
        v-if="showDevSection"
        xs12>
        <material-card
          title="07 開發人員填寫（開發填寫時必填）"
          color="deep-orange"
          class="card-padding">
          <div class="caption mb-2">待開發、開發中、驗收退回時填寫實際日期。填完後用下方的送出或拒絕。</div>
          <v-layout wrap>
            <v-flex
              xs12
              lg6>
              <v-text-field
                :value="form.developerNames"
                :class="fieldClass"
                label="開發人員（自動帶入）"
                placeholder="審核通過後帶入"
                disabled
              />
            </v-flex>
            <v-flex
              xs12
              lg3>
              <v-menu
                v-model="dateMenu.actualStartDate"
                :close-on-content-click="false"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                max-width="290px">
                <v-text-field
                  slot="activator"
                  v-model="form.actualStartDate"
                  :class="fieldClass"
                  :disabled="!devEditable"
                  label="實際開始日期 *"
                  prepend-icon="event"
                  readonly
                  clearable
                />
                <v-date-picker
                  v-model="form.actualStartDate"
                  :color="theme==='dark'?'danger':'general'"
                  no-title
                  scrollable
                  @input="dateMenu.actualStartDate = false"
                />
              </v-menu>
            </v-flex>
            <v-flex
              xs12
              lg3>
              <v-menu
                v-model="dateMenu.actualEndDate"
                :close-on-content-click="false"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                max-width="290px">
                <v-text-field
                  slot="activator"
                  v-model="form.actualEndDate"
                  :class="fieldClass"
                  :disabled="!devEditable"
                  label="實際結束日期 *"
                  prepend-icon="event"
                  readonly
                  clearable
                />
                <v-date-picker
                  v-model="form.actualEndDate"
                  :color="theme==='dark'?'danger':'general'"
                  no-title
                  scrollable
                  @input="dateMenu.actualEndDate = false"
                />
              </v-menu>
            </v-flex>
          </v-layout>
        </material-card>
      </v-flex>

      <v-flex
        v-if="showQa"
        xs12>
        <material-card
          title="開發問題"
          color="deep-orange"
          class="card-padding">
          <div class="caption mb-2">開發人員填寫問題。退回後，審核人員和需求人員只能填寫回答。</div>
          <div
            v-for="(qa, index) in devQaList"
            :key="'qa' + index"
            class="req-qa">
            <div
              v-if="qa.authorName"
              class="caption">{{ qa.authorName }}</div>
            <v-textarea
              v-model="qa.question"
              :class="fieldClass"
              :disabled="!qaEditable"
              label="問題"
              rows="2"
            />
            <v-textarea
              v-model="qa.answer"
              :class="fieldClass"
              :disabled="!answerEditable"
              label="回答"
              rows="2"
            />
            <div
              v-if="index < devQaList.length - 1"
              class="req-qa-line"></div>
          </div>
          <v-btn
            v-if="qaEditable"
            flat
            color="general"
            @click="devQaList.push({ question: '', answer: '', notifyUserPkList: [], qaStatus: 'DRAFT' })">
            <v-icon class="mr-2">mdi-plus</v-icon>新增問題
          </v-btn>
        </material-card>
      </v-flex>

      <v-flex
        v-if="canReview"
        xs12>
        <material-card
          title="指定開發人員"
          color="deep-orange"
          class="card-padding">
          <div class="caption mb-2">審核同意前要先指定開發人員，可勾選多人。</div>
          <v-layout
            wrap
            align-center>
            <v-flex
              xs12
              lg8>
              <v-text-field
                :value="form.developerNames || assignedDeveloperText"
                :class="fieldClass"
                label="指定開發人員 *"
                placeholder="尚未選擇"
                readonly
                @click.native="openDeveloperDialog"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-btn
                color="general"
                @click="openDeveloperDialog">選擇開發人員</v-btn>
            </v-flex>
          </v-layout>
        </material-card>
      </v-flex>

      <v-flex
        v-if="showApprovalFlow"
        xs12>
        <material-card
          title="簽核流程"
          color="deep-orange"
          class="card-padding req-approval-card">
          <v-data-table
            :headers="approvalHeaders"
            :items="approvalList"
            :pagination.sync="approvalPagination"
            hide-actions
            class="elevation-0 req-approval-table">
            <template
              slot="items"
              slot-scope="props">
              <tr>
                <td>{{ props.item.actionName }}</td>
                <td>{{ props.item.signerName }}</td>
                <td>{{ props.item.signTime }}</td>
                <td>{{ props.item.comment }}</td>
              </tr>
            </template>
            <template slot="no-data">
              <span>尚無簽核紀錄</span>
            </template>
          </v-data-table>
        </material-card>
      </v-flex>

      <v-flex
        xs12
        class="req-footer">
        <div class="req-footer-bar">
          <div
            v-if="showActionComment"
            class="req-footer-note">
            <v-textarea
              v-model="actionComment"
              :class="fieldClass"
              label="說明"
              placeholder="退回或拒絕時請填寫說明"
              rows="2"
              hide-details
            />
          </div>
          <div class="req-footer-actions">
            <v-btn
              v-if="formEditable"
              color="general"
              @click="saveDraft">暫存草稿</v-btn>
            <v-btn
              v-if="formEditable"
              color="pink"
              @click="submitTicket">送出</v-btn>
            <v-btn
              v-if="canReview"
              color="general"
              @click="runAction('REVIEW_PASS')">審核同意</v-btn>
            <v-btn
              v-if="canReview"
              flat
              color="red"
              @click="runAction('REVIEW_REJECT')">審核退回</v-btn>
            <v-btn
              v-if="canCancel"
              flat
              color="red"
              @click="doCancel">取消需求單</v-btn>
            <v-btn
              v-if="devEditable"
              color="general"
              @click="saveDev">儲存開發內容</v-btn>
            <v-btn
              v-if="devEditable"
              color="pink"
              @click="runAction('DEV_PASS')">送出</v-btn>
            <v-btn
              v-if="devEditable"
              flat
              color="red"
              @click="runAction('DEV_RETURN')">拒絕</v-btn>
            <v-btn
              v-if="canAccept"
              color="general"
              @click="runAction('ACCEPT_CLOSE')">驗收關閉</v-btn>
            <v-btn
              v-if="canAccept"
              flat
              color="red"
              @click="runAction('ACCEPT_REJECT')">驗收退回</v-btn>
          </div>
        </div>
      </v-flex>
    </v-layout>

    <input
      ref="file"
      type="file"
      style="display:none"
      @change="onFile">

    <v-dialog
      v-model="questionDialog"
      content-class="req-skin req-question-dialog"
      max-width="1040"
      persistent>
      <v-card>
        <v-card-title class="headline">來源資料規範與異常</v-card-title>
        <v-card-text>
          <div
            v-if="questionBank.length === 0"
            class="body-1">題庫目前沒有題目。沒有資料時，之後有題目再填 NA 即可。</div>
          <div
            v-for="q in questionBank"
            :key="'qq' + q.pk"
            class="req-q">
            <div class="req-q-num">{{ q.qCode }}</div>
            <div class="req-q-body">
              <div class="req-q-title">{{ q.qText }}<span v-if="q.required === 1"> *</span></div>
              <div
                v-if="q.hint"
                class="req-q-story">{{ q.hint }}</div>
              <v-select
                v-if="q.qType === 'SELECT'"
                v-model="answerOf(q.pk).answerText"
                :items="optionItems(q)"
                :class="fieldClass"
                :disabled="!formEditable"
                :placeholder="q.placeholder || '請選擇'"
                label=""
              />
              <v-textarea
                v-else-if="q.qType === 'TEXTAREA'"
                v-model="answerOf(q.pk).answerText"
                :class="fieldClass"
                :disabled="!formEditable"
                :placeholder="q.placeholder || '沒有則填 NA'"
                rows="3"
              />
              <v-text-field
                v-else
                v-model="answerOf(q.pk).answerText"
                :class="fieldClass"
                :disabled="!formEditable"
                :placeholder="q.placeholder || '沒有則填 NA'"
              />
              <div class="req-attach">
                <v-btn
                  v-if="formEditable"
                  class="req-dashed"
                  flat
                  @click="pickQuestionFile(q.pk)">附件檔案（可選）</v-btn>
                <span v-if="answerOf(q.pk).fileName && !answerOf(q.pk).filePk">{{ answerOf(q.pk).fileName }}</span>
                <v-btn
                  v-if="answerOf(q.pk).filePk"
                  class="req-dashed"
                  flat
                  @click="downloadFile({ pk: answerOf(q.pk).filePk, fileName: answerOf(q.pk).fileName || '附件' })">下載 {{ answerOf(q.pk).fileName || '附件' }}</v-btn>
                <v-btn
                  v-if="formEditable && (answerOf(q.pk).filePk || answerOf(q.pk).localId)"
                  flat
                  color="red"
                  @click="removeQuestionFile(q.pk)">移除</v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="general"
            flat
            @click="questionDialog = false">關閉</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="developerDialog"
      content-class="req-skin"
      max-width="720"
      persistent>
      <v-card>
        <v-card-title class="headline">指定開發人員</v-card-title>
        <v-card-text>
          <v-layout
            wrap
            align-center>
            <v-flex
              xs12
              lg8>
              <v-text-field
                v-model="developerKeyword"
                :class="fieldClass"
                label="開發人員"
                placeholder="可輸入用戶名"
                @keyup.enter="searchDevelopers"
              />
            </v-flex>
            <v-flex
              xs12
              lg4>
              <v-btn
                color="general"
                @click="searchDevelopers">查詢</v-btn>
            </v-flex>
          </v-layout>
          <v-data-table
            v-model="developerPick"
            :headers="developerHeaders"
            :items="developerRows"
            item-key="pk"
            select-all
            class="elevation-0">
            <template
              slot="headers"
              slot-scope="props">
              <tr>
                <th style="width: 56px">
                  <v-checkbox
                    :input-value="props.all"
                    :indeterminate="props.indeterminate"
                    primary
                    hide-details
                    @click.stop="toggleDeveloperAll"
                  />
                </th>
                <th
                  v-for="header in props.headers"
                  :key="header.value">{{ header.text }}</th>
              </tr>
            </template>
            <template
              slot="items"
              slot-scope="props">
              <tr
                :active="props.selected"
                @click="props.selected = !props.selected">
                <td @click.stop>
                  <v-checkbox
                    :input-value="props.selected"
                    primary
                    hide-details
                    @click.native.stop="props.selected = !props.selected"
                  />
                </td>
                <td>{{ props.item.name }}</td>
              </tr>
            </template>
            <template slot="no-data">
              <span>請輸入用戶名後查詢</span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            flat
            @click="developerDialog = false">關閉</v-btn>
          <v-btn
            color="general"
            @click="confirmDevelopers">確認</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="loadingView"
      :width="'calc(100% - ' + shrink + 'px)'"
      class="chart-view"
      persistent>
      <p style="letter-spacing: 5px;text-align: center !important;color: #ffffff;background-color: #000000dd;padding: 15px;width: 50%;margin: auto;">
        <img
          :src="Loading"
          style="width: 2rem;margin-bottom: 10px;">
        <br>
        LOADING 載入中
      </p>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      :top="true"
      :color="snackbarColor">
      {{ snackbarMsg }}
      <v-btn
        dark
        flat
        @click="snackbar = false">
        Close
      </v-btn>
    </v-snackbar>
  </v-container>
</template>
<script>
import { mapState } from 'vuex'
import Loading from '@/assets/img/loading.gif'

const FileBlock = {
  name: 'FileBlock',
  props: {
    files: { type: Array, default: function () { return [] } },
    editable: { type: Boolean, default: false },
    label: { type: String, default: '附件' }
  },
  render: function (h) {
    const self = this
    const chips = (this.files || []).map(function (file) {
      const children = [file.fileName]
      if (self.editable) {
        children.push(h('v-icon', {
          props: { right: true, small: true },
          on: { click: function (event) { event.stopPropagation(); self.$emit('remove', file) } }
        }, 'mdi-close'))
      }
      if (file.pk) {
        children.unshift(h('v-btn', {
          class: 'mr-1',
          props: { flat: true, small: true, color: 'general' },
          on: { click: function (event) { event.stopPropagation(); self.$emit('download', file) } }
        }, '下載'))
      }
      return h('v-chip', {
        key: file.pk || file.localId,
        class: 'mr-2',
        on: { click: function () { if (file.pk) self.$emit('download', file) } }
      }, children)
    })
    const nodes = [h('div', { class: 'body-1 mb-1' }, this.label)].concat(chips)
    if (this.editable) {
      nodes.push(h('v-btn', {
        props: { flat: true, color: 'general' },
        on: { click: function () { self.$emit('add') } }
      }, '上傳'))
    }
    return h('div', { class: 'mt-2 mb-2' }, nodes)
  }
}

function emptyBg () {
  return {
    currentProblem: '',
    goal: '',
    reqSource: '',
    reqSourceRemark: '',
    noDoImpact: '',
    factoryName: '',
    dataFormat: '',
    uploadFreq: '',
    sampleLogic: '',
    dataCategory: ''
  }
}

function emptyDisplay () {
  return {
    needChart: 0,
    chartTypeList: [],
    chartOther: '',
    chartAxisDesc: '',
    chartListRelation: '',
    needExport: 0,
    exportSameAsPage: 1,
    exportExtraHidden: 0,
    exportRemark: ''
  }
}

export default {
  name: 'RequirementTicketForm',
  components: { FileBlock },
  data () {
    const login = JSON.parse(localStorage.getItem('user') || '{}')
    const openingTicket = !!(this.$route.query && this.$route.query.ticketNo)
    return {
      Loading,
      loadingView: false,
      snackbar: false,
      snackbarColor: 'red',
      snackbarMsg: '',
      userPk: login.userPk,
      dateMenu: {
        reqDate: false,
        planStartDate: false,
        planEndDate: false,
        actualStartDate: false,
        actualEndDate: false
      },
      developers: [],
      namedDevelopers: [],
      notifyUsers: [],
      secondary: false,
      questionDialog: false,
      questionBank: [],
      answerMap: {},
      relatedItems: [],
      versionItems: [],
      selectedVersion: '',
      attachmentMap: {},
      uploadBizType: '',
      uploadBizRefNo: null,
      pendingQuestionPk: null,
      pendingFiles: [],
      developerDialog: false,
      developerKeyword: '',
      developerRows: [],
      developerPick: [],
      assignedDevelopers: [],
      developerHeaders: [{ text: '姓名', value: 'name', sortable: false }],
      actionComment: '',
      form: {
        ticketNo: '',
        version: '',
        status: '',
        statusName: '',
        isCurrent: 1,
        major: null,
        reqType: 'NEW_FEATURE',
        title: '',
        priority: '',
        requesterName: openingTicket ? '' : (login.displayName || ''),
        requesterDeptName: openingTicket ? '' : (login.departmentName || ''),
        extension: '',
        reqDate: '',
        planStartDate: '',
        planEndDate: '',
        relatedTicketNo: '',
        developerNames: '',
        requesterDeptPk: null,
        developerUserPkList: [],
        actualStartDate: '',
        actualEndDate: ''
      },
      bg: emptyBg(),
      display: emptyDisplay(),
      termList: [{ termName: '', termDesc: '' }],
      queryCondList: [{ headerName: '', requiredBasic: 0, condDesc: '' }],
      resultColList: [{ columnName: '', columnDesc: '' }],
      extraList: [{ extraDesc: '' }],
      moduleList: [{ moduleName: '', originalBehavior: '', expectedChange: '', changeReason: '' }],
      devQaList: [],
      approvalList: [],
      approvalPagination: { rowsPerPage: -1 },
      approvalHeaders: [
        { text: '簽核', value: 'actionName', sortable: false },
        { text: '簽核人', value: 'signerName', sortable: false },
        { text: '簽核時間', value: 'signTime', sortable: false },
        { text: '說明', value: 'comment', sortable: false }
      ],
      reqTypeItems: [
        { text: '新增功能', value: 'NEW_FEATURE' },
        { text: '修改功能', value: 'MODIFY' },
        { text: '問題回報', value: 'ISSUE' }
      ],
      priorityItems: [
        { text: 'P0 正常開發', value: 'P0' },
        { text: 'P1 影響日常作業', value: 'P1' },
        { text: 'P2 非常緊急', value: 'P2' }
      ],
      yesNoItems: [
        { text: '否', value: 0 },
        { text: '是', value: 1 }
      ],
      chartTypeItems: [
        { text: '折線圖', value: 'LINE' },
        { text: '柱狀圖', value: 'BAR' },
        { text: '餅圖', value: 'PIE' },
        { text: '其他', value: 'OTHER' }
      ],
      chartRelationItems: [
        { text: '圖表獨立展示', value: 'INDEPENDENT' },
        { text: '點擊圖表連動列表', value: 'LINK_TO_LIST' },
        { text: '無需關聯', value: 'NONE' }
      ]
    }
  },
  computed: {
    ...mapState('app', ['theme', 'shrink']),
    fieldClass () {
      return { 'danger-input': this.theme === 'dark', 'general-input': this.theme === 'light' }
    },
    formEditable () {
      if (this.secondary) return true
      if (!this.form.ticketNo) return true
      if (Number(this.form.isCurrent) === 0) return false
      return !this.form.status || this.form.status === 'DRAFT' || this.form.status === 'REVIEW_REJECTED'
    },
    readOnlyReason () {
      if (this.formEditable) return ''
      if (Number(this.form.isCurrent) === 0) return '這是歷史版本，內容不能修改。請改看目前版本。'
      if (this.form.statusName) return '目前狀態是「' + this.form.statusName + '」。需求內容只在草稿和審核退回時可以改。'
      return ''
    },
    showTicketNo () {
      return !!this.form.ticketNo && !!this.form.status && this.form.status !== 'DRAFT'
    },
    versionText () {
      if (this.form.version) return this.form.version
      if (this.secondary && this.form.major != null && this.form.major !== '') {
        return 'V' + (Number(this.form.major) + 1) + '.0'
      }
      return 'V1.0'
    },
    devEditable () {
      if (Number(this.form.isCurrent) === 0) return false
      return ['PENDING_DEV', 'IN_DEV', 'ACCEPTANCE_REJECTED'].indexOf(this.form.status) >= 0
    },
    canReview () {
      return Number(this.form.isCurrent) !== 0 &&
        (this.form.status === 'PENDING_REVIEW' || this.form.status === 'DEV_RETURNED')
    },
    canAccept () {
      return Number(this.form.isCurrent) !== 0 && this.form.status === 'PENDING_ACCEPTANCE'
    },
    showActionComment () {
      return this.canReview || this.devEditable || this.canAccept
    },
    canCancel () {
      if (!this.form.ticketNo || this.secondary) return false
      if (Number(this.form.isCurrent) === 0) return false
      return ['DRAFT', 'PENDING_REVIEW', 'REVIEW_REJECTED'].indexOf(this.form.status) >= 0
    },
    showDevSection () {
      return !!this.form.ticketNo && !this.secondary &&
        ['PENDING_DEV', 'IN_DEV', 'DEV_RETURNED', 'PENDING_ACCEPTANCE', 'ACCEPTANCE_REJECTED', 'CLOSED'].indexOf(this.form.status) >= 0
    },
    showQa () {
      if (!this.form.ticketNo || this.secondary || !this.form.status || this.form.status === 'CANCELLED') return false
      if (this.qaEditable) return true
      return (this.devQaList || []).length > 0
    },
    qaEditable () {
      if (Number(this.form.isCurrent) === 0) return false
      return ['PENDING_DEV', 'IN_DEV', 'ACCEPTANCE_REJECTED'].indexOf(this.form.status) >= 0
    },
    answerEditable () {
      if (Number(this.form.isCurrent) === 0) return false
      if (this.qaEditable) return false
      return this.formEditable || this.canReview || this.canAccept
    },
    assignedDeveloperText () {
      return (this.assignedDevelopers || []).map(user => user.name).filter(Boolean).join('、')
    },
    showApprovalFlow () {
      if (this.formEditable || this.secondary || !this.form.ticketNo) return false
      return true
    }
  },
  mounted () {
    this.loadFormFont()
    this.$store.dispatch('getAuthorize', '/system/requirementTicketForm')
      .then(response => {
        if (response.status === 'success' && response.data.accessible) {
          this.bootstrap()
        } else if (response.errCode === 'E10000002') {
          localStorage.setItem('errormsg', response.errMsg)
          window.location.assign(this.$router.resolve({ path: '/noEnter/E10000002' }).href)
        } else {
          window.location.assign(this.$router.resolve({ path: JSON.parse(localStorage.getItem('user')).home }).href)
        }
      })
      .catch(() => {
        window.location.assign(this.$router.resolve({ path: JSON.parse(localStorage.getItem('user')).home }).href)
      })
  },
  methods: {
    loadFormFont () {
      if (document.getElementById('req-form-font')) return
      const link = document.createElement('link')
      link.id = 'req-form-font'
      link.rel = 'stylesheet'
      link.href = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=Noto+Sans+TC:wght@400;500;700&display=swap'
      document.head.appendChild(link)
    },
    toast (msg, ok) {
      this.snackbarMsg = msg
      this.snackbarColor = ok ? 'green' : 'red'
      this.snackbar = true
    },
    bootstrap () {
      this.secondary = this.$route.query.secondary === '1'
      const login = JSON.parse(localStorage.getItem('user') || '{}')
      this.userPk = login.userPk
      if (!this.$route.query.ticketNo) {
        this.form.requesterName = login.displayName || ''
        this.form.requesterDeptName = login.departmentName || ''
        this.loadQuestions()
      } else {
        this.loadContent(this.$route.query.ticketNo, this.secondary ? '' : (this.$route.query.version || ''))
      }
      this.loadRelated()
    },
    loadRelated () {
      this.$http.post('/api/system/req/queryRelatedTicket', {}).then(res => res.data)
        .then(res => {
          if (res.status !== 'success') return
          this.relatedItems = (res.data || []).map(row => ({
            text: row.ticketNo + ' ' + (row.title || '') + ' ' + row.version,
            value: row.ticketNo
          }))
        })
    },
    loadQuestions () {
      this.$http.post('/api/system/req/getQuestionBank', {}).then(res => res.data)
        .then(res => {
          console.info(res)
          if (res.status === 'success') this.questionBank = res.data || []
        })
    },
    loadContent (ticketNo, version) {
      this.loadingView = true
      this.$http.post('/api/system/req/getTicketContent', { ticketNo: ticketNo, version: version || '' }).then(res => res.data)
        .then(res => {
          this.loadingView = false
          if (res.status !== 'success') {
            this.toast(res.errMsg || '讀取失敗')
            return
          }
          this.applyContent(res.data)
          this.loadVersions(ticketNo)
        })
        .catch(() => {
          this.loadingView = false
          this.toast('讀取失敗')
        })
    },
    loadVersions (ticketNo) {
      this.$http.post('/api/system/req/queryTicketVersionList', { ticketNo: ticketNo }).then(res => res.data)
        .then(res => {
          if (res.status !== 'success') return
          this.versionItems = (res.data || []).map(row => ({
            text: row.version + ' ' + (row.statusName || ''),
            value: row.version
          }))
        })
    },
    applyContent (data) {
      const ticket = data.ticket || {}
      this.form.ticketNo = ticket.ticketNo || ''
      this.form.version = ticket.version || ''
      this.form.status = ticket.status || ''
      this.form.statusName = ticket.statusName || ''
      this.form.isCurrent = ticket.isCurrent
      this.form.major = ticket.major
      this.form.reqType = ticket.reqType || 'NEW_FEATURE'
      this.form.title = ticket.title || ''
      this.form.priority = ticket.priority || 'P0'
      this.form.requesterName = ticket.requesterName || ''
      this.form.requesterDeptName = ticket.requesterDeptName || ''
      this.form.extension = ticket.extension || ''
      this.form.reqDate = ticket.reqDate || ''
      this.form.planStartDate = ticket.planStartDate || ''
      this.form.planEndDate = ticket.planEndDate || ''
      this.form.relatedTicketNo = ticket.relatedTicketNo || ''
      this.form.requesterDeptPk = ticket.requesterDeptPk
      this.form.developerUserPkList = ticket.developerUserPkList || []
      this.form.developerNames = ticket.developerNames || ''
      this.developers = data.developers || []
      this.namedDevelopers = data.assignedDevelopers || []
      this.notifyUsers = data.notifyUsers || []
      this.form.actualStartDate = ticket.actualStartDate || ''
      this.form.actualEndDate = ticket.actualEndDate || ''
      this.selectedVersion = this.form.version
      this.bg = Object.assign(emptyBg(), data.bg || {})
      this.display = Object.assign(emptyDisplay(), data.display || {})
      if (!this.display.chartTypeList) this.display.chartTypeList = []
      this.termList = (data.termList && data.termList.length) ? data.termList : [{ termName: '', termDesc: '' }]
      const conds = (data.queryCondList || []).map(row => {
        row.requiredBasic = row.requiredBasic === true || Number(row.requiredBasic) === 1 ? 1 : 0
        return row
      })
      this.queryCondList = conds.length ? conds : [{ headerName: '', requiredBasic: 0, condDesc: '' }]
      this.resultColList = (data.resultColList && data.resultColList.length) ? data.resultColList : [{ columnName: '', columnDesc: '' }]
      this.extraList = (data.extraList && data.extraList.length) ? data.extraList : [{ extraDesc: '' }]
      this.moduleList = (data.moduleList && data.moduleList.length) ? data.moduleList : [{ moduleName: '', originalBehavior: '', expectedChange: '', changeReason: '' }]
      this.attachmentMap = data.attachmentMap || {}
      this.questionBank = data.questionBank || []
      this.answerMap = {}
      const answerFiles = (data.attachmentMap && data.attachmentMap.DS_ANSWER) || []
      ;(data.answerList || []).forEach(row => {
        const matched = answerFiles.find(file => Number(file.pk) === Number(row.filePk))
        this.$set(this.answerMap, row.questionPk, {
          answerText: row.answerText || '',
          filePk: row.filePk || null,
          fileName: matched ? matched.fileName : ''
        })
      })
      this.devQaList = (data.devQaList || []).map(row => {
        row.notifyUserPkList = String(row.notifyUserPks || '').split(',').filter(Boolean).map(id => Number(id))
        return row
      })
      const approvalNames = {
        SUBMIT: '送出',
        CANCEL: '取消需求單',
        REVIEW_PASS: '審核同意',
        REVIEW_REJECT: '審核退回',
        DEV_PASS: '開發送出',
        DEV_RETURN: '開發拒絕',
        ACCEPT_CLOSE: '驗收關閉',
        ACCEPT_REJECT: '驗收退回',
        SECONDARY: '二次開發'
      }
      this.approvalList = (data.approvalList || []).map(row => {
        row.actionName = approvalNames[row.action] || row.action || ''
        row.signerName = row.signerName || ''
        row.signTime = row.signTime || ''
        row.comment = row.comment || ''
        return row
      })
      if (this.secondary) {
        this.form.version = ''
        this.form.status = ''
        this.form.statusName = ''
        this.form.isCurrent = 1
        if (this.form.reqType === 'NEW_FEATURE') this.form.reqType = 'MODIFY'
        this.form.relatedTicketNo = this.form.relatedTicketNo || this.form.ticketNo
        this.attachmentMap = {}
        Object.keys(this.answerMap).forEach(key => {
          this.answerMap[key].filePk = null
          this.answerMap[key].fileName = ''
        })
        this.form.actualStartDate = ''
        this.form.actualEndDate = ''
        this.form.developerUserPkList = []
        this.form.developerNames = ''
        this.devQaList = []
        this.approvalList = []
      }
      this.syncAssignedDevelopers()
    },
    syncAssignedDevelopers () {
      const names = {}
      ;(this.namedDevelopers || []).forEach(user => {
        names[String(user.pk)] = user.name
      })
      ;(this.developers || []).forEach(user => {
        if (!names[String(user.userPk)]) names[String(user.userPk)] = user.userName
      })
      this.assignedDevelopers = (this.form.developerUserPkList || []).map(pk => ({
        pk: pk,
        name: names[String(pk)] || String(pk)
      }))
      const joined = this.assignedDevelopers.map(user => user.name).filter(Boolean).join('、')
      if (joined && !/^\d+(、\d+)*$/.test(joined)) this.form.developerNames = joined
    },
    openDeveloperDialog () {
      this.developerKeyword = ''
      this.developerRows = (this.assignedDevelopers || []).map(user => ({ pk: user.pk, name: user.name }))
      this.developerPick = this.developerRows.slice()
      this.developerDialog = true
    },
    searchDevelopers () {
      this.loadingView = true
      this.$http.post('/api/system/req/queryDeveloper', {
        name: (this.developerKeyword || '').trim()
      }).then(res => res.data)
        .then(res => {
          console.info(res)
          this.loadingView = false
          if (res.status !== 'success') {
            this.toast(res.errMsg || '查詢失敗')
            return
          }
          const found = res.data || []
          const ids = {}
          found.forEach(row => { ids[String(row.pk)] = true })
          const kept = (this.developerPick || []).filter(row => !ids[String(row.pk)])
          this.developerRows = kept.concat(found)
        })
        .catch(() => {
          this.loadingView = false
          this.toast('查詢失敗')
        })
    },
    toggleDeveloperAll () {
      const ids = {}
      this.developerRows.forEach(row => { ids[String(row.pk)] = true })
      const selected = {}
      this.developerPick.forEach(row => { selected[String(row.pk)] = true })
      const allOn = this.developerRows.length > 0 && this.developerRows.every(row => selected[String(row.pk)])
      if (allOn) {
        this.developerPick = this.developerPick.filter(row => !ids[String(row.pk)])
      } else {
        const extra = this.developerPick.filter(row => !ids[String(row.pk)])
        this.developerPick = extra.concat(this.developerRows.slice())
      }
    },
    confirmDevelopers () {
      if (!this.developerPick.length) {
        this.toast('請勾選開發人員')
        return
      }
      this.assignedDevelopers = this.developerPick.map(row => ({ pk: row.pk, name: row.name }))
      this.form.developerUserPkList = this.assignedDevelopers.map(row => row.pk)
      this.form.developerNames = this.assignedDevelopers.map(row => row.name).filter(Boolean).join('、')
      this.developerDialog = false
    },
    toggleChart (code, checked) {
      const list = (this.display.chartTypeList || []).slice()
      const index = list.indexOf(code)
      if (checked && index < 0) list.push(code)
      if (!checked && index >= 0) list.splice(index, 1)
      this.display.chartTypeList = list
    },
    answerOf (questionPk) {
      if (!this.answerMap[questionPk]) {
        this.$set(this.answerMap, questionPk, { answerText: '', filePk: null, fileName: '', localId: null })
      }
      return this.answerMap[questionPk]
    },
    pickQuestionFile (questionPk) {
      this.pendingQuestionPk = questionPk
      this.pickFile('DS_ANSWER', questionPk)
    },
    removeQuestionFile (questionPk) {
      const answer = this.answerOf(questionPk)
      if (answer.localId) {
        this.pendingFiles = this.pendingFiles.filter(item => item.localId !== answer.localId)
        answer.localId = null
        answer.fileName = ''
        return
      }
      if (!answer.filePk) return
      const filePk = answer.filePk
      this.loadingView = true
      this.$http.post('/api/system/req/deleteAttachment', { userPk: this.userPk, pk: filePk }).then(res => res.data)
        .then(res => {
          this.loadingView = false
          if (res.status !== 'success') {
            this.toast(res.errMsg || '刪除失敗')
            return
          }
          answer.filePk = null
          answer.fileName = ''
        })
        .catch(() => {
          this.loadingView = false
          this.toast('刪除失敗')
        })
    },
    optionItems (question) {
      return (question.options || []).map(opt => ({ text: opt.optLabel, value: opt.optValue }))
    },
    filesOf (bizType) {
      const saved = (this.attachmentMap && this.attachmentMap[bizType]) || []
      const waiting = (this.pendingFiles || []).filter(file => file.bizType === bizType)
      return saved.concat(waiting)
    },
    filesOfRef (bizType, sortNo) {
      return this.filesOf(bizType).filter(file => Number(file.bizRefNo) === Number(sortNo))
    },
    buildPayload () {
      const answers = []
      Object.keys(this.answerMap).forEach(key => {
        const row = this.answerMap[key]
        if (!row) return
        answers.push({ questionPk: Number(key), answerText: row.answerText, filePk: row.filePk || null })
      })
      const openSecondary = (this.form.reqType === 'MODIFY' || this.form.reqType === 'ISSUE') && !this.form.version
      const ticketNo = openSecondary ? (this.form.relatedTicketNo || this.form.ticketNo) : this.form.ticketNo
      return {
        userPk: this.userPk,
        ticketNo: ticketNo,
        version: openSecondary ? '' : this.form.version,
        secondary: openSecondary,
        reqType: this.form.reqType,
        title: this.form.title,
        extension: this.form.extension,
        reqDate: this.form.reqDate,
        planStartDate: this.form.planStartDate,
        planEndDate: this.form.planEndDate,
        priority: this.form.priority,
        relatedTicketNo: this.form.relatedTicketNo,
        actualStartDate: this.form.actualStartDate,
        actualEndDate: this.form.actualEndDate,
        bg: this.bg,
        termList: this.termList,
        answerList: answers,
        display: this.display,
        queryCondList: this.queryCondList,
        resultColList: this.resultColList,
        extraList: this.extraList,
        moduleList: this.moduleList
      }
    },
    applyIds (data) {
      if (!data || !data.ticketNo) return
      this.form.ticketNo = data.ticketNo
      this.form.version = data.version
      this.form.status = data.status
      this.form.statusName = data.statusName
      this.form.isCurrent = 1
      this.secondary = false
      this.selectedVersion = data.version
      this.$router.replace({ path: '/system/requirementTicketForm', query: { ticketNo: data.ticketNo, version: data.version } }).catch(() => {})
    },
    persistDraft () {
      return this.$http.post('/api/system/req/saveTicketDraft', this.buildPayload()).then(res => res.data)
        .then(res => {
          if (res.data && res.data.ticketNo) this.applyIds(res.data)
          if (res.status !== 'success') {
            this.toast(res.errMsg || '暫存失敗')
            return false
          }
          return true
        })
        .catch(() => {
          this.toast('暫存失敗')
          return false
        })
    },
    postFile (item) {
      const body = new FormData()
      body.append('userPk', this.userPk)
      body.append('ticketNo', this.form.ticketNo)
      body.append('version', this.form.version)
      body.append('bizType', item.bizType)
      if (item.bizRefNo != null) body.append('bizRefNo', item.bizRefNo)
      body.append('file', item.file)
      return this.$http.post('/api/system/req/uploadAttachment', body).then(res => res.data)
        .then(res => {
          if (res.status !== 'success') {
            this.toast(res.errMsg || '上傳失敗')
            return null
          }
          return res.data
        })
        .catch(() => {
          this.toast('上傳失敗')
          return null
        })
    },
    flushPending () {
      const list = (this.pendingFiles || []).slice()
      if (!list.length) return Promise.resolve({ ok: true, linkedQuestion: false })
      let linkedQuestion = false
      const next = (index) => {
        if (index >= list.length) return Promise.resolve({ ok: true, linkedQuestion: linkedQuestion })
        const item = list[index]
        return this.postFile(item).then(data => {
          if (!data) return { ok: false, linkedQuestion: false }
          this.pendingFiles = this.pendingFiles.filter(row => row.localId !== item.localId)
          if (item.bizType === 'DS_ANSWER') {
            const answer = this.answerOf(item.questionPk)
            answer.filePk = data.pk
            answer.fileName = data.fileName
            answer.localId = null
            linkedQuestion = true
          }
          return next(index + 1)
        })
      }
      return next(0)
    },
    saveDraft () {
      this.loadingView = true
      return this.persistDraft().then(ok => {
        if (!ok) return null
        return this.flushPending()
      }).then(flushed => {
        if (!flushed || !flushed.ok) return false
        if (!flushed.linkedQuestion) return true
        return this.persistDraft()
      }).then(ok => {
        this.loadingView = false
        if (!ok) return false
        this.toast('已暫存', true)
        this.loadContent(this.form.ticketNo, this.form.version)
        return true
      })
    },
    submitTicket () {
      this.loadingView = true
      const ready = (this.form.ticketNo && this.form.version && !this.secondary)
        ? Promise.resolve(true)
        : this.persistDraft()
      ready.then(ok => {
        if (!ok) return null
        return this.flushPending()
      }).then(flushed => {
        if (!flushed || !flushed.ok) return false
        if (!flushed.linkedQuestion) return true
        return this.persistDraft()
      }).then(ok => {
        if (!ok) {
          this.loadingView = false
          return
        }
        const readyQa = this.answerEditable ? this.saveQaList() : Promise.resolve(true)
        readyQa.then(saved => {
          if (!saved) {
            this.loadingView = false
            return
          }
          this.$http.post('/api/system/req/submitTicket', this.buildPayload()).then(res => res.data)
            .then(res => {
              this.loadingView = false
              if (res.data && res.data.ticketNo) this.applyIds(res.data)
              if (res.status !== 'success') {
                this.toast(res.errMsg || '送出失敗')
                if (this.form.ticketNo) this.loadContent(this.form.ticketNo, this.form.version)
                return
              }
              this.toast('已送出', true)
              this.backToList()
            })
            .catch(() => {
              this.loadingView = false
              this.toast('送出失敗')
            })
        })
      })
    },
    saveDev () {
      this.loadingView = true
      const body = this.buildPayload()
      this.$http.post('/api/system/req/updateTicket', body).then(res => res.data)
        .then(res => {
          this.loadingView = false
          if (res.status !== 'success') {
            this.toast(res.errMsg || '儲存失敗')
            return
          }
          this.toast('已儲存開發內容', true)
          if (res.data) this.applyIds(res.data)
          this.loadContent(this.form.ticketNo, this.form.version)
        })
        .catch(() => {
          this.loadingView = false
          this.toast('儲存失敗')
        })
    },
    doCancel () {
      if (!window.confirm('確定取消這張需求單？')) return
      this.loadingView = true
      this.$http.post('/api/system/req/cancelTicket', {
        userPk: this.userPk,
        ticketNo: this.form.ticketNo,
        version: this.form.version
      }).then(res => res.data)
        .then(res => {
          this.loadingView = false
          if (res.status !== 'success') {
            this.toast(res.errMsg || '取消失敗')
            return
          }
          this.toast('已取消', true)
          this.loadContent(this.form.ticketNo, this.form.version)
        })
        .catch(() => {
          this.loadingView = false
          this.toast('取消失敗')
        })
    },
    runAction (code) {
      const names = {
        REVIEW_PASS: '審核同意',
        REVIEW_REJECT: '審核退回',
        DEV_PASS: '送出',
        DEV_RETURN: '拒絕',
        ACCEPT_CLOSE: '驗收關閉',
        ACCEPT_REJECT: '驗收退回'
      }
      const needsComment = ['REVIEW_REJECT', 'DEV_RETURN', 'ACCEPT_REJECT'].indexOf(code) >= 0
      if (needsComment && !String(this.actionComment || '').trim()) {
        this.toast('請填寫說明')
        return
      }
      const developers = (this.assignedDevelopers || []).map(user => user.pk)
      if (code === 'REVIEW_PASS' && developers.length === 0) {
        this.toast('請指派開發人員')
        return
      }
      if (!window.confirm('是否確認' + (names[code] || '簽核') + '？')) return
      this.loadingView = true
      const needQa = code === 'DEV_PASS' || code === 'DEV_RETURN' || this.answerEditable
      const ready = needQa ? this.saveQaList() : Promise.resolve(true)
      ready.then(ok => {
        if (!ok) {
          this.loadingView = false
          return
        }
        let url = '/api/system/req/reviewTicket'
        let action = 'PASS'
        if (code === 'REVIEW_REJECT') action = 'REJECT'
        if (code === 'DEV_PASS' || code === 'DEV_RETURN') {
          url = '/api/system/req/developTicket'
          action = code === 'DEV_PASS' ? 'PASS' : 'REJECT'
        }
        if (code === 'ACCEPT_CLOSE' || code === 'ACCEPT_REJECT') {
          url = '/api/system/req/acceptTicket'
          action = code === 'ACCEPT_CLOSE' ? 'CLOSE' : 'REJECT'
        }
        this.$http.post(url, {
          userPk: this.userPk,
          ticketNo: this.form.ticketNo,
          version: this.form.version,
          action: action,
          comment: this.actionComment,
          developerUserPkList: developers,
          actualStartDate: this.form.actualStartDate,
          actualEndDate: this.form.actualEndDate
        }).then(res => res.data)
          .then(res => {
            this.loadingView = false
            if (res.status !== 'success') {
              this.toast(res.errMsg || '簽核失敗')
              return
            }
            this.actionComment = ''
            this.toast('已完成', true)
            this.backToList()
          })
          .catch(() => {
            this.loadingView = false
            this.toast('簽核失敗')
          })
      })
    },
    changeVersion (version) {
      if (!version || version === this.form.version) return
      this.secondary = false
      this.loadContent(this.form.ticketNo, version)
    },
    pickFile (bizType, bizRefNo) {
      this.uploadBizType = bizType
      this.uploadBizRefNo = bizRefNo == null ? null : bizRefNo
      this.$refs.file.click()
    },
    onFile (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const forQuestion = this.uploadBizType === 'DS_ANSWER'
      const questionPk = this.pendingQuestionPk
      if (!this.form.ticketNo || this.secondary || !this.form.version) {
        const localId = Date.now() + '-' + Math.random()
        this.pendingFiles.push({
          localId: localId,
          fileName: file.name,
          bizType: this.uploadBizType,
          bizRefNo: this.uploadBizRefNo,
          questionPk: forQuestion ? questionPk : null,
          file: file
        })
        if (forQuestion) {
          const answer = this.answerOf(questionPk)
          answer.fileName = file.name
          answer.localId = localId
          answer.filePk = null
        }
        this.toast('已選擇檔案，暫存或送出時一併上傳', true)
        return
      }
      const previousPk = forQuestion ? this.answerOf(questionPk).filePk : null
      this.loadingView = true
      this.postFile({
        bizType: this.uploadBizType,
        bizRefNo: this.uploadBizRefNo,
        file: file
      }).then(data => {
        this.loadingView = false
        if (!data) return
        if (forQuestion) {
          const answer = this.answerOf(questionPk)
          answer.filePk = data.pk
          answer.fileName = data.fileName
          this.toast('已上傳', true)
          if (previousPk && Number(previousPk) !== Number(data.pk)) {
            this.$http.post('/api/system/req/deleteAttachment', { userPk: this.userPk, pk: previousPk })
          }
          return
        }
        this.toast('已上傳', true)
        this.loadContent(this.form.ticketNo, this.form.version)
      })
    },
    downloadFile (file) {
      this.$http.get('/api/system/req/downloadAttachment', { params: { pk: file.pk }, responseType: 'blob' })
        .then(res => {
          const blob = res.data
          if (blob && blob.type && blob.type.indexOf('json') >= 0) {
            this.toast('下載失敗')
            return
          }
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = file.fileName || 'download'
          link.click()
          window.URL.revokeObjectURL(url)
        })
        .catch(() => this.toast('下載失敗'))
    },
    removeFile (file) {
      if (file.localId) {
        this.pendingFiles = this.pendingFiles.filter(item => item.localId !== file.localId)
        return
      }
      this.loadingView = true
      this.$http.post('/api/system/req/deleteAttachment', { userPk: this.userPk, pk: file.pk }).then(res => res.data)
        .then(res => {
          this.loadingView = false
          if (res.status !== 'success') {
            this.toast(res.errMsg || '刪除失敗')
            return
          }
          this.loadContent(this.form.ticketNo, this.form.version)
        })
        .catch(() => {
          this.loadingView = false
          this.toast('刪除失敗')
        })
    },
    saveQaList () {
      const answerOnly = !this.qaEditable
      const rows = (this.devQaList || []).filter(qa => {
        if (!String(qa.question || '').trim()) return false
        if (answerOnly && !qa.pk) return false
        return true
      })
      const next = (index) => {
        if (index >= rows.length) return Promise.resolve(true)
        const qa = rows[index]
        return this.$http.post('/api/system/req/saveDevQa', {
          userPk: this.userPk,
          ticketNo: this.form.ticketNo,
          version: this.form.version,
          pk: qa.pk || null,
          question: qa.question,
          answer: qa.answer,
          notifyUserPkList: [],
          qaStatus: 'SUBMITTED',
          answerOnly: answerOnly
        }).then(res => res.data)
          .then(res => {
            if (res.status !== 'success') {
              this.toast(res.errMsg || '儲存開發問題失敗')
              return false
            }
            if (res.data && res.data.pk) qa.pk = res.data.pk
            return next(index + 1)
          })
          .catch(() => {
            this.toast('儲存開發問題失敗')
            return false
          })
      }
      return next(0)
    },
    backToList () {
      this.$router.push({ path: '/system/requirementTicket' })
    }
  }
}
</script>

<style>
.req-skin {
  --paper: #161211;
  --ink: #f3eee9;
  --steel: #b3a89f;
  --line: #3c322d;
  --navy: #d21f3c;
  --navy-dark: #a4162e;
  --rust: #e0932f;
  --rust-soft: #2c2013;
  --card: #201a18;
  --input-bg: #241d1a;
  font-family: 'IBM Plex Sans', 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
  color: var(--ink);
}
.req-skin.req-ticket-form {
  background-color: var(--paper);
  background-image:
    radial-gradient(circle at 8% 100%, rgba(210, 31, 60, 0.2), transparent 55%),
    linear-gradient(var(--line) 1px, transparent 1px);
  background-size: 100% 100%, 100% 40px;
  background-attachment: fixed, fixed;
  min-height: calc(100vh - 88px);
  padding-bottom: 28px !important;
}
.req-skin,
.req-skin .v-card,
.req-skin .v-btn,
.req-skin input,
.req-skin textarea,
.req-skin .v-label,
.req-skin .v-select__selection {
  font-family: 'IBM Plex Sans', 'Noto Sans TC', 'Microsoft JhengHei', sans-serif !important;
}
.req-skin .v-card {
  background: var(--card) !important;
  color: var(--ink) !important;
  border: 1px solid var(--line);
  box-shadow: none !important;
}
.req-skin .v-card__text,
.req-skin .v-card__title,
.req-skin .v-card__actions {
  color: var(--ink) !important;
}
.req-skin .v-card--material__header.v-card {
  background: linear-gradient(90deg, var(--navy), var(--navy-dark)) !important;
  border: none !important;
  border-radius: 2px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
}
.req-skin .v-card--material__header .title {
  color: #fff !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em;
}
.req-skin .body-1,
.req-skin .subheading,
.req-skin .headline {
  color: var(--ink) !important;
}
.req-skin .body-1 {
  font-size: 14px !important;
  color: var(--steel) !important;
}
.req-skin .caption {
  color: var(--steel) !important;
  font-size: 13px !important;
}
.req-skin .subheading,
.req-skin .headline {
  font-size: 15px !important;
  font-weight: 600 !important;
}
.req-skin .v-alert {
  background: var(--rust-soft) !important;
  border: 1px solid #4a3820 !important;
  border-left: 4px solid var(--rust) !important;
  color: #e8c896 !important;
}
.req-skin .v-label {
  color: var(--steel) !important;
  font-size: 13px !important;
}
.req-skin .v-text-field {
  padding-top: 22px;
  margin-top: 8px;
}
.req-skin .v-text-field .v-label,
.req-skin .v-text-field .v-label--active {
  top: 2px !important;
  transform: translateY(-20px) scale(1) !important;
  max-width: 100% !important;
  font-size: 12px !important;
  font-weight: 500;
  color: var(--steel) !important;
}
.req-skin .v-input--selection-controls .v-label {
  color: var(--ink) !important;
  font-size: 15px !important;
}
.req-skin .v-input input,
.req-skin .v-input textarea,
.req-skin .v-select__selection {
  color: var(--ink) !important;
  font-size: 15px !important;
}
.req-skin .v-input input::placeholder,
.req-skin .v-input textarea::placeholder {
  color: var(--steel) !important;
  opacity: 0.8;
}
.req-skin .v-text-field > .v-input__control > .v-input__slot {
  background: var(--input-bg) !important;
  border: 1px solid var(--line) !important;
  border-radius: 2px;
  padding: 2px 10px !important;
  min-height: 44px;
}
.req-skin .v-text-field > .v-input__control > .v-input__slot::before,
.req-skin .v-text-field > .v-input__control > .v-input__slot::after {
  display: none !important;
}
.req-skin .v-input--is-focused > .v-input__control > .v-input__slot {
  border-color: var(--navy) !important;
  background: #2a2220 !important;
  box-shadow: 0 0 0 1px var(--navy);
}
.req-skin .v-input--is-disabled input,
.req-skin .v-input--is-disabled textarea,
.req-skin .v-input--is-disabled .v-select__selection {
  color: #8c8178 !important;
}
.req-skin .v-input__icon .v-icon,
.req-skin .v-input .v-icon {
  color: var(--steel) !important;
}
.req-skin .v-input--selection-controls .accent--text,
.req-skin .v-input--is-label-active.v-input--selection-controls .v-icon {
  color: var(--navy) !important;
}
.req-skin .v-messages,
.req-skin .v-counter {
  color: var(--steel) !important;
}
.req-skin .v-messages__message {
  color: var(--rust) !important;
}
.req-skin .v-chip {
  background: var(--input-bg) !important;
  border: 1px solid var(--line) !important;
  color: var(--ink) !important;
}
.req-skin .v-chip .v-chip__content {
  color: var(--ink) !important;
  font-size: 13px !important;
}
.req-skin .v-divider {
  border-color: var(--line) !important;
}
.req-skin .v-btn {
  border-radius: 2px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  height: 40px;
  text-transform: none;
}
.req-skin .v-btn:not(.v-btn--flat):not(.v-btn--icon) {
  background: transparent !important;
  color: var(--ink) !important;
  border: 1px solid var(--navy) !important;
  box-shadow: none !important;
}
.req-skin .v-btn:not(.v-btn--flat):not(.v-btn--icon):hover {
  background: rgba(210, 31, 60, 0.16) !important;
}
.req-skin .v-btn.pink:not(.v-btn--flat) {
  background: var(--navy) !important;
  border-color: var(--navy) !important;
  color: #fff !important;
}
.req-skin .v-btn.pink:not(.v-btn--flat):hover {
  background: var(--navy-dark) !important;
}
.req-skin .v-btn--flat {
  background: transparent !important;
  color: var(--ink) !important;
  border: 1px solid var(--line) !important;
  box-shadow: none !important;
}
.req-skin .v-btn--flat:hover {
  background: rgba(255, 255, 255, 0.06) !important;
}
.req-skin .v-btn--flat.red--text {
  color: #e8c896 !important;
  border-color: #4a3820 !important;
}
.req-skin .v-btn .v-icon {
  color: inherit !important;
  font-size: 18px !important;
}
.req-skin .req-footer {
  position: sticky;
  bottom: 0;
  z-index: 4;
  margin-top: 12px;
  background: var(--card);
  border: 1px solid var(--line);
  box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.3);
  padding: 12px 16px !important;
  text-align: left !important;
}
.req-skin .req-footer-bar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}
.req-skin .req-footer-note {
  flex: 1;
  min-width: 240px;
}
.req-skin .req-qa-line {
  border-bottom: 1px solid var(--line);
  margin: 8px 0 18px;
}
.req-skin .req-footer-actions {
  margin-left: auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
}
.req-skin .v-snackbar .v-btn {
  background: transparent !important;
  border: none !important;
  color: #fff !important;
  height: 36px !important;
  box-shadow: none !important;
  font-weight: 500 !important;
}
.req-question-dialog {
  width: 1040px;
  max-width: 96vw;
  max-height: 88vh;
  margin: 16px auto !important;
}
.req-skin.req-question-dialog > .v-card {
  min-height: 72vh;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.req-skin.req-question-dialog .v-card__text {
  overflow: auto;
}
.req-skin .req-q {
  display: flex;
  gap: 16px;
  border-top: 1px solid var(--line);
  padding: 22px 4px;
  margin-bottom: 0;
}
.req-skin .req-q-num {
  width: 72px;
  flex: none;
  font-family: 'IBM Plex Mono', Consolas, monospace;
  font-size: 22px;
  font-weight: 600;
  color: var(--steel);
  padding-top: 2px;
}
.req-skin .req-q-body {
  flex: 1;
  min-width: 0;
}
.req-skin .req-q-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 10px;
}
.req-skin .req-q-story {
  background: var(--rust-soft);
  border-left: 3px solid var(--rust);
  padding: 10px 14px;
  font-size: 13px;
  color: #e8c896;
  margin-bottom: 12px;
}
.req-skin .req-attach {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
}
.req-skin .req-dashed.v-btn {
  border: 1px dashed var(--line) !important;
  background: transparent !important;
  color: var(--steel) !important;
}
.req-skin.v-dialog > .v-card > .v-card__title {
  background: linear-gradient(90deg, var(--navy), var(--navy-dark));
}
.req-skin .v-datatable {
  background: #241d1a;
  color: #f3eee9;
}
.req-skin .v-datatable thead th {
  color: #b3a89f !important;
}
.req-skin .v-datatable .v-datatable__actions {
  color: #b3a89f;
}
.req-skin .req-approval-card > .v-card__text {
  padding-left: 16px !important;
  padding-right: 16px !important;
}
.req-skin .req-approval-table,
.req-skin .req-approval-table .v-table__overflow {
  width: 100%;
}
.req-skin .req-approval-table table.v-table {
  width: 100% !important;
  max-width: 100% !important;
  table-layout: fixed;
}
.req-skin .req-approval-table table.v-table thead th,
.req-skin .req-approval-table table.v-table tbody td {
  height: auto !important;
  line-height: 22px !important;
  padding: 11px 16px !important;
  font-size: 15px !important;
  white-space: normal;
  vertical-align: middle;
  box-sizing: border-box;
}
.req-skin .req-approval-table table.v-table th:nth-child(1),
.req-skin .req-approval-table table.v-table td:nth-child(1) {
  width: 16%;
}
.req-skin .req-approval-table table.v-table th:nth-child(2),
.req-skin .req-approval-table table.v-table td:nth-child(2) {
  width: 18%;
}
.req-skin .req-approval-table table.v-table th:nth-child(3),
.req-skin .req-approval-table table.v-table td:nth-child(3) {
  width: 24%;
}
.req-skin .req-approval-table table.v-table th:nth-child(4),
.req-skin .req-approval-table table.v-table td:nth-child(4) {
  width: 42%;
}
.req-skin.v-dialog > .v-card > .v-card__title,
.req-skin.v-dialog > .v-card > .v-card__title .headline {
  color: #fff !important;
}
</style>
